<template>
    <LoadingState :loading="loadingWallet" />

    <WalletFilterBar
        @addAccount="openConnectModal"
        :refreshing="refreshingWallet"
        v-if="walletEntities.length || !loadingWallet"
    />
    <component
        :is="isMobile ? WalletTableMobile : WalletTable"
        @add-account="openConnectModal"
        @edit="handleEditFromTable"
        v-if="walletEntities.length || !loadingWallet"
    />

    <EditCardModal
        :card="selectedCard"
        :user="selectedUser"
        :users="walletUsers"
        :cards="cards"
        :wallet="wallet"
        :banks="banks"
        @close="closeEditCardModal"
        @update="handleCardUpdated"
        :show="showEditCardModal"
    />

    <CopyCardDetailsModal
        v-if="showCopyCardModal"
        :show="showCopyCardModal"
        :source-card="{ card: selectedCard, user: selectedUser }"
        @close="closeCopyCardModal"
    />

    <ConnectModal
        :wallet="wallet"
        :showAddCardModal="showAddCardModal"
        :accountSyncComplete="accountSyncComplete"
        :showUnresolvedFixer="showUnresolvedFixer"
        :addCardMethod="addCardMethod"
        :cards="cards"
        :users="visibleUsers"
        :banks="banks"
        :step="connectStep"
        :activeUser="activeUser"
        @finished="finishedConnect"
        @close="closeConnectModal"
        @addedCard="addedCard"
    />

    <add-wallet-user-modal />

    <UnmatchedModal
        :is-open="showUnmatchedModal"
        @close="closeUnmatchedModal"
        :unmatchCount="unmatchedCardCount"
    />

    <BusinessAddEditModal
        :open="showBusinessAddEditModal"
        :users="walletUsers"
        v-model:businessModel="businessEntity"
        @close="closeBusinessAddEditModal"
        @add-business="handleAddUpdateBusiness"
        @update-business="handleAddUpdateBusiness"
    />
    <ManageBusinessesModal
        :open="checkModalToggle"
        @close="closeManageBusinessesModal"
        @add-new-business="openAddBusinessModal"
        @edit-business="openEditBusinessModal"
        @show-delete-confirm="openDeleteConfirmModal"
    />
    <BusinessDeleteConfirmModal
        :open="showDeleteConfirmModal"
        @close="closeDeleteConfirmModal"
        @deleteBusiness="handleDeleteBusiness"
        :business="businessEntity"
    />
    <WalletCardMatchModal
        :show="showCardMatchModal"
        @close="closeCardMatchModal"
        @card-matched="handleCardMatched"
        @confirm="handleConfirmMatchUpdates"
    />
</template>
<script setup>
import {
    computed,
    onMounted,
    onUnmounted,
    ref,
    watch,
    watchEffect,
    nextTick,
} from 'vue';
import { storeToRefs } from 'pinia';
import { useIsMobile } from '@/composables/useIsMobile';
import AddWalletUserModal from '@/components/wallet/modals/add-wallet-user.vue';
import ConnectModal from '@/components/wallet/modals/connect.vue';
import CopyCardDetailsModal from '@/components/wallet/modals/copy-card-details.vue';
import EditCardModal from '@/components/wallet/modals/edit-card-modal.vue';
import UnmatchedModal from '@/components/wallet/modals/unmatched-modal.vue';
import WalletFilterBar from '@/components/wallet/partials/wallet-filter-bar.vue';
import WalletTable from '@/components/wallet/partials/wallet-table.vue';
import WalletTableMobile from '@/components/wallet/wallet-table-mobile.vue';
import LoadingState from '@/components/ui/loading-state/loading-state.vue';
import BusinessAddEditModal from '@/components/business-entities/business-entities-add-edit-modal.vue';
import ManageBusinessesModal from '@/components/business-entities/manage-business-entities.modal.vue';
import BusinessDeleteConfirmModal from '@/components/business-entities/business-entities-delete-confirm-modal.vue';
import WalletCardMatchModal from '@/components/wallet/modals/wallet-card-match-modal.vue';
import { useWalletStore } from '@/store/wallet';
import { usePage } from '@inertiajs/vue3';

const walletStore = useWalletStore();
const {
    getWalletByEntities,
    setShowCopyCardModal,
    setShowEditCardModal,
    setSelectedCard,
    setSelectedUser,
    setShowBusinessAddEditModal,
    setShowManageBusinessesModal,
    setShowDeleteConfirmModal,
    setBusinessEntity,
    resetBusinessEntity,
    addBusiness,
    updateBusiness,
    deleteBusiness,
    getBusinessEntitiesByUser,
    setShowCardMatchModal,
    openCardMatchModal,
    setAccountSidebar,
} = walletStore;

const {
    banks,
    businessEntity,
    cards,
    loadingWallet,
    refreshingWallet,
    selectedCard,
    selectedUser,
    showCopyCardModal,
    showEditCardModal,
    showBusinessAddEditModal,
    showManageBusinessesModal,
    showDeleteConfirmModal,
    showCardMatchModal,
    walletUsers,
    walletEntities,
    wallet,
    showAccountSidebar,
    connectModalRequested,
} = storeToRefs(walletStore);

const showUnmatchedModal = ref(false);
const accountSyncComplete = ref(false);
const showUnresolvedFixer = ref(false);
const showAddCardModal = ref(false);
const showAddBusinessModal = ref(false);
const connectStep = ref(1);
const addCardMethod = ref('');
const activeUser = ref({});
const { isMobile } = useIsMobile();

// Refs for DOM elements
const walletTableContainerRef = ref(null);
const walletColHeaderRef = ref(null);
const fBodyRef = ref(null);

let walletScrollEventListener = null;
let walletTableContainerListener = null;
let sidebarOpenTimeoutId = null;

const checkModalToggle = computed(() => {
    if (showManageBusinessesModal.value === true) {
        return true;
    }
    return showAddBusinessModal.value || showManageBusinessesModal.value;
});

const visibleUsers = computed(() => walletUsers.value.filter((u) => u.show));

const unmatchedCardCount = computed(() => {
    return wallet.value.unresolved_cards
        ? wallet.value.unresolved_cards.length
        : 0;
});

const closeCardMatchModal = () => {
    setSelectedCard(null);
    setShowCardMatchModal(false);
};

const handleConfirmMatchUpdates = () => {
    getWalletByEntities(null, { background: true });
};

const handleCardUpdated = ({ card, user }) => {
    getWalletByEntities(null, { background: true });
    setSelectedCard(card);
    setSelectedUser(user);
};

const handleCardMatched = async (card) => {
    if (!card) {
        console.error('Card data is empty or undefined');
        return;
    }

    try {
        const cardCopy = { ...card };

        await getWalletByEntities(null, { background: true });
        setSelectedCard(cardCopy);

        if (selectedCard.value) {
            setShowEditCardModal(true);
        } else {
            console.error(
                'Selected card is not properly set after wallet update'
            );
        }
    } catch (error) {
        console.error('Error updating wallet:', error);
    }
};

// SUPPORT-739: bootstrapWallet only runs on mount, so a tab left open keeps
// rendering its mount-time payload indefinitely (one support tab was 13 days
// stale and offered delete on business entities that no longer existed).
// Revalidate in the background whenever the tab becomes visible again and the
// in-memory shell has gone stale.
const revalidateOnVisible = () => {
    if (document.visibilityState === 'visible') {
        walletStore.revalidateWalletIfStale();
    }
};

onMounted(async () => {
    document.addEventListener('visibilitychange', revalidateOnVisible);
    // MY1-2830: hydrate instantly from the per-user IndexedDB cache when present
    // (subtle indicator only), otherwise cold-load with the full-page spinner.
    const userUuid = usePage().props.userData?.uuid ?? null;
    await walletStore.bootstrapWallet(userUuid);
    const editingCard = checkForCardToEdit();
    if (!editingCard) {
        checkUnresolvedCards();
    }
    checkHash();
    checkUrlParams();
});

// Use watchEffect to reactively find DOM elements and set up event listeners
// This runs when loadingWallet changes and elements become available
watchEffect(async () => {
    // Wait for loading to complete and DOM to be ready
    if (loadingWallet.value) {
        return;
    }

    await nextTick();

    // Try to find the DOM elements
    const walletTableContainer = document.getElementById(
        'wallettablecontainer'
    );
    const walletColHeader = document.getElementById('wallet-col-header');
    const fBody = document.getElementById('f-body');

    // Update refs
    walletTableContainerRef.value = walletTableContainer;
    walletColHeaderRef.value = walletColHeader;
    fBodyRef.value = fBody;

    // Clean up existing listeners
    if (walletScrollEventListener && fBody) {
        fBody.removeEventListener('scroll', walletScrollEventListener);
        walletScrollEventListener = null;
    }
    if (walletTableContainerListener && walletTableContainer) {
        walletTableContainer.removeEventListener(
            'scroll',
            walletTableContainerListener
        );
        walletTableContainerListener = null;
    }

    // Only set up listeners if all elements are available
    if (!walletTableContainer || !walletColHeader || !fBody) {
        return;
    }

    // Set up scroll listener for f-body
    const scrollHandler = function () {
        const walletTableCoords = walletTableContainer.getBoundingClientRect();
        if (walletTableCoords.top <= 112) {
            const viewportWidth = window.innerWidth;
            const leftPosition = Math.max(0, walletTableCoords.left);
            const maxWidth = viewportWidth - leftPosition;
            const headerWidth = Math.min(walletTableCoords.width, maxWidth);

            walletTableContainer.style.paddingTop = '42px';
            walletColHeader.style.position = 'fixed';
            walletColHeader.style.top = '112px';
            walletColHeader.style.left = leftPosition + 'px';
            walletColHeader.style.width = headerWidth + 'px';
            walletColHeader.style.zIndex = '30';
            // SUPPORT-639: hide the sticky header's own horizontal scrollbar.
            // With 'auto' the header gained an independent scrollbar that was
            // not synced with the data container — dragging it scrolled only
            // the column titles and left the rows misaligned. The bottom
            // scrollbar on #wallettablecontainer remains the single control;
            // `containerScrollHandler` below keeps the header in lockstep by
            // assigning scrollLeft programmatically, which still works with
            // overflow:hidden.
            walletColHeader.style.overflowX = 'hidden';
            walletColHeader.style.scrollBehavior = 'auto';
        } else {
            walletTableContainer.style.paddingTop = '0';
            walletColHeader.style.position = 'static';
            walletColHeader.style.top = 'auto';
            walletColHeader.style.left = 'auto';
            walletColHeader.style.width = 'auto';
            walletColHeader.style.zIndex = 'auto';
            walletColHeader.style.overflowX = 'inherit';
            walletColHeader.style.scrollBehavior = 'smooth';
        }
    };

    // Set up scroll listener for wallet table container
    const containerScrollHandler = function () {
        if (walletColHeader.style.position === 'fixed') {
            walletColHeader.scrollLeft = walletTableContainer.scrollLeft;
        }
    };

    fBody.addEventListener('scroll', scrollHandler);
    walletTableContainer.addEventListener('scroll', containerScrollHandler);

    // Store references for cleanup
    walletScrollEventListener = scrollHandler;
    walletTableContainerListener = containerScrollHandler;
});

onUnmounted(() => {
    document.removeEventListener('visibilitychange', revalidateOnVisible);
    // Clean up event listeners
    if (walletScrollEventListener && fBodyRef.value) {
        fBodyRef.value.removeEventListener('scroll', walletScrollEventListener);
    }
    if (walletTableContainerListener && walletTableContainerRef.value) {
        walletTableContainerRef.value.removeEventListener(
            'scroll',
            walletTableContainerListener
        );
    }
    // Clear sidebar timeout to prevent state updates after unmount
    if (sidebarOpenTimeoutId) {
        clearTimeout(sidebarOpenTimeoutId);
    }
});

// Business-related methods
const openAddBusinessForm = () => {
    setShowBusinessAddEditModal(true);
    resetBusinessEntity();
};

const openManageBusinesses = () => {
    resetBusinessEntity();
    setShowManageBusinessesModal(true);
};

const closeBusinessAddEditModal = () => {
    setShowBusinessAddEditModal(false);
    resetBusinessEntity();
};

const closeManageBusinessesModal = () => {
    setShowManageBusinessesModal(false);
    resetBusinessEntity();
};

const handleAddUpdateBusiness = async (businessData) => {
    try {
        if (!businessData.id) {
            await addBusiness(businessData);
        } else {
            await updateBusiness(businessData);
        }
        // Background refresh keeps the table visible; the manage-businesses modal
        // tracks refreshingWallet for its own spinner. SUPPORT-739: the manage
        // modal renders the fresh entities list, so refresh it alongside the
        // wallet payload after any mutation.
        await Promise.all([
            getWalletByEntities(null, { background: true }),
            getBusinessEntitiesByUser(),
        ]);
        setShowBusinessAddEditModal(false);
        resetBusinessEntity();
    } catch (error) {
        console.error('Request failed!');
    }
};

const openAddBusinessModal = () => {
    resetBusinessEntity();
    setShowManageBusinessesModal(false);
    setShowBusinessAddEditModal(true);
};

const openEditBusinessModal = (businessData) => {
    setShowManageBusinessesModal(false);
    setShowBusinessAddEditModal(true);
    setBusinessEntity(businessData);
};

const closeDeleteConfirmModal = async () => {
    setShowDeleteConfirmModal(false);
    await getWalletByEntities(null, { background: true });
    resetBusinessEntity();
};

const openDeleteConfirmModal = (businessData) => {
    setBusinessEntity(businessData);
    setShowDeleteConfirmModal(true);
};

const handleDeleteBusiness = async (businessData) => {
    try {
        await deleteBusiness(businessData);
        // SUPPORT-739: the manage modal stays open behind the delete confirm
        // and renders the fresh entities list, so refresh that list too or the
        // just-deleted row keeps rendering until the modal is reopened.
        await Promise.all([
            getWalletByEntities(null, { background: true }),
            getBusinessEntitiesByUser(),
        ]);
        setShowDeleteConfirmModal(false);
        resetBusinessEntity();
    } catch (error) {
        console.error('Request failed!');
    }
};

const checkForCardToEdit = () => {
    const storageKey = '10x_temp_account_to_edit';
    const storedData = localStorage.getItem(storageKey);
    try {
        const storedData = localStorage.getItem(storageKey);

        if (storedData) {
            localStorage.removeItem(storageKey);

            const data = JSON.parse(storedData);

            if (
                !data ||
                !data.guid ||
                !data.timestamp ||
                data.source !== 'connection_card'
            ) {
                console.warn('Invalid account edit data structure');
                return;
            }

            const now = Date.now();
            const maxAge = 30 * 1000;

            if (now - data.timestamp > maxAge) {
                console.warn('Account edit request expired');
                return;
            }

            const accountGuid = data.guid;

            let foundAccount = null;
            walletEntities.value.forEach((user) => {
                user.institutions.forEach((institution) => {
                    institution.accounts.forEach((account) => {
                        if (account.guid === accountGuid) {
                            foundAccount = account;
                        }
                    });
                });
            });

            if (foundAccount) {
                // Unmatched accounts (no card_id) go through the card-match
                // flow — the edit modal has no card picker for them (SUPPORT-726).
                if (foundAccount.card_id) {
                    setSelectedCard(foundAccount);
                    setShowEditCardModal(true);
                } else {
                    openCardMatchModal(foundAccount);
                }
                return true;
            }
        }
    } catch (error) {
        console.error('Error processing account edit data:', error);
        localStorage.removeItem(storageKey);
    }
    return false;
};

const checkHash = () => {
    if (location.hash && location.hash === '#add-account') {
        openConnectModal();
        history.pushState(
            '',
            document.title,
            window.location.pathname + window.location.search
        );
    }
};

const checkUrlParams = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const cardId = urlParams.get('card_id');
    const openSidebar = urlParams.get('open_sidebar');
    const matchCardId = urlParams.get('match_card');
    const unmatchedCardId = urlParams.get('unmatched_card');

    // MY1-3047: the dashboard widget's "View cards" link lands here needing
    // a specific column visible (the user may have hidden it). One-time
    // reveal — deliberately not persisted to display preferences.
    const showColumn = urlParams.get('show_column');
    if (
        showColumn &&
        walletStore.columns.some((column) => column.id === showColumn) &&
        !walletStore.visibleColumns.includes(showColumn)
    ) {
        walletStore.visibleColumns.push(showColumn);
    }

    const findAccount = (accountId) => {
        let foundCard = null;
        let foundUser = null;
        walletEntities.value.forEach((user) => {
            user.institutions.forEach((institution) => {
                institution.accounts.forEach((account) => {
                    if (account.id === parseInt(accountId)) {
                        foundCard = account;
                        foundUser = user;
                    }
                });
            });
        });
        return { foundCard, foundUser };
    };

    if (cardId) {
        const { foundCard, foundUser } = findAccount(cardId);
        if (foundCard) {
            setSelectedCard(foundCard);
            if (foundUser) {
                setSelectedUser(foundUser);
            }
            if (openSidebar === 'true') {
                // Delay opening sidebar by 500ms for smooth transition from Best Cards
                sidebarOpenTimeoutId = setTimeout(() => {
                    setAccountSidebar(true);
                }, 500);
            }
        }
    }

    if (matchCardId) {
        // Deep link from NoCardMatch / RejectedResolvedAccount notifications
        // → open the missing card request modal directly on step 2 (the
        // Missing Card Request form), skipping the card-search step.
        const { foundCard, foundUser } = findAccount(matchCardId);
        if (foundCard) {
            if (foundUser) {
                setSelectedUser(foundUser);
            }
            walletStore.openCardMatchModalAtRequestForm(foundCard);
        }
    }

    if (unmatchedCardId) {
        // Deep link from the owner's UnmatchedCardsFound notification → open
        // the match modal on the card-search step; the product usually exists
        // under another name, so the request form is only the fallback.
        const { foundCard, foundUser } = findAccount(unmatchedCardId);
        if (foundCard) {
            if (foundUser) {
                setSelectedUser(foundUser);
            }
            openCardMatchModal(foundCard);
        }
    }

    if (cardId || matchCardId || unmatchedCardId) {
        // Clean up URL params after handling deep links
        history.replaceState({}, document.title, window.location.pathname);
    }
};

const checkUnresolvedCards = () => {
    if (unmatchedCardCount.value > 0) {
        // The "I'll do it later" button sets the `matchaccounts` cookie for 14
        // days; honor it as the single source of truth. (Previously an always-on
        // `has_new_accounts` flag re-forced the modal every load despite the
        // cookie, so dismissing it never stuck.)
        if (!$cookies.get('matchaccounts')) {
            showUnmatchedModal.value = true;
        }
    }
};

const openConnectModal = () => {
    connectStep.value = 1;
    accountSyncComplete.value = false;
    showUnresolvedFixer.value = false;
    addCardMethod.value = '';
    showAddCardModal.value = true;
};

const finishedConnect = () => {
    showAddCardModal.value = false;
    connectStep.value = 1;
    accountSyncComplete.value = false;
    showUnresolvedFixer.value = false;
    addCardMethod.value = '';
};

const addedCard = async () => {
    showAddCardModal.value = false;
    await getWalletByEntities(null, { background: true });
};

const closeConnectModal = () => {
    showAddCardModal.value = false;
};

// MY1-3044: deep components (card-details banner, bonus-widget tip) request
// the connect flow through the store; open it and reset the flag. Immediate,
// so a request raised on another page before navigating here (the benefits
// "Auto-tracking available" label, MY1-3079) opens the modal on arrival.
// Declared after openConnectModal on purpose: an immediate watcher runs
// synchronously during setup, and the const it calls must already exist.
watch(
    connectModalRequested,
    (requested) => {
        if (requested) {
            openConnectModal();
            walletStore.connectModalRequested = false;
        }
    },
    { immediate: true }
);

const closeCopyCardModal = () => {
    clearSelectedData();
    setShowCopyCardModal(false);
};

const closeEditCardModal = () => {
    setShowEditCardModal(false);
    connectStep.value = 1;
    if (!showAccountSidebar.value) {
        clearSelectedData();
    }
};

const handleEditFromTable = (cardData) => {
    // Find the card in the wallet
    const card = wallet.value?.connected_accounts?.find(
        (account) => account.id === cardData.id
    );
    if (card) {
        setSelectedCard(card);
        // If editing a product change, store it for the modal
        if (cardData.editingProductChange) {
            walletStore.setEditingProductChange(cardData.editingProductChange);
        }
        setShowEditCardModal(true);
    }
};

const clearSelectedData = () => {
    setSelectedCard(null);
    setSelectedUser(null);
};

const closeUnmatchedModal = () => {
    showUnmatchedModal.value = false;
};
</script>
